import React from 'react';
import ReactDOM from 'react-dom';
import DatePicker from 'react-datepicker';
import './themed-datepicker.scss';

// Portal the calendar to <body> so an ancestor's overflow:hidden (rounded cards)
// or transform (entrance animations) can't clip it. Paired with popperProps
// strategy:'fixed' so it sits relative to the viewport.
const PopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  ReactDOM.createPortal(children, document.body);

// The parent contract is always a yyyy-mm-dd string; convert only at the boundary.
const parseIso = (s?: string): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const toIso = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface ThemedDatePickerProps {
  /** yyyy-mm-dd string (or '' / undefined when empty). */
  value?: string;
  /** Receives a yyyy-mm-dd string (or '' when cleared). */
  onChange: (value: string) => void;
  hasError?: boolean;
  maxDate?: Date;
  minDate?: Date;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Shared violet-themed date picker (react-datepicker). Read-only input
 * (click-to-open, no typing), portal-rendered calendar, month/year scroll
 * dropdowns. Keep `value` as a yyyy-mm-dd string everywhere outside the picker.
 */
export const ThemedDatePicker: React.FC<ThemedDatePickerProps> = ({
  value,
  onChange,
  hasError,
  maxDate,
  minDate,
  disabled,
  placeholder = 'yyyy/mm/dd',
}) => (
  <DatePicker
    selected={parseIso(value)}
    onChange={(d: Date | null) => onChange(toIso(d))}
    dateFormat="yyyy/MM/dd"
    placeholderText={placeholder}
    maxDate={maxDate}
    minDate={minDate}
    disabled={disabled}
    className={`form-control tdp__input${hasError ? ' error tdp__input--error' : ''}`}
    wrapperClassName="tdp__input-dp"
    popperContainer={PopperPortal}
    popperProps={{ strategy: 'fixed' }}
    popperClassName="tdp__dp-popper"
    calendarClassName="tdp__dp-calendar"
    onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
    showMonthDropdown
    showYearDropdown
    dropdownMode="scroll"
    autoComplete="off"
  />
);

export default ThemedDatePicker;
